
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../contexts/AuthContext";
import { AppContext } from "../../../contexts/AppContext";
import { EditDataSetSidebar } from "./EditDataSetSidebar";
import DataItems from "./DataItems";
import CoreDetails from "./CoreDetails";
import MetadataDetails from "./MetadataDetails";
import IngestWorkflow from "./IngestWorkflow";
import { DynamicWorkflowGraphTab } from "../../../components/Workflows/DynamicWorkflowGraphTab";
import { normalizeMetadataErrors } from "../../../utils/datasetMetadata";
import { SchemaExportDialog } from "../../../components/Metadata/SchemaExportDialog";
import { BackLink, Button, CancelButton, PageTitle, PageHeader } from "../../../components/StyledElements";
import { getDataSet, runIngestionWorkflow } from "../../../services/data"
import Loading from "../../../components/Loading";
import swal from "sweetalert2";

import { useDataSets } from "../../../contexts/DataSetsContext";
import { resolveGeneratorClientId } from "../../../utils/workflowGenerationRequest";

export const EditDataSet = () => {
  const { activeUser } = useAuthContext();
  const { selectedClient } = useContext(AppContext);
  const navigate = useNavigate();
  const { data_set_id } = useParams();
  const [selectedTab, setSelectedTab] = useState(window.location.hash ? window.location.hash.substr(1) : "details");

  const { selectedDataSet, setUpdatedDataSet, updatedDataSet, canSaveDataSet, saveUpdatedDataSet } = useDataSets();
  const [savingDataSet, setSavingDataSet] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showSchemaExport, setShowSchemaExport] = useState(false);

  useEffect(() => {
    // Fetch live event details using data_set_id
    console.log("!!! FETCHING DATASET ID: ", data_set_id);
    const fetchDataSet = async () => {
      const results = await getDataSet(data_set_id, true);
      if (results.success) {
        const dataSet = results.data_set;
        console.log("!!! data SET: ", dataSet);
        setUpdatedDataSet(dataSet);
        console.log("!!! DATA SET: ", dataSet);
      } else {
        swal.fire({
          title: "Error",
          text: results.message || "Failed to fetch data set details.",
          icon: "error",
        }).then(() => {
          if (activeUser?.user_type.match(/client/)) {
            navigate(`/client/data/#data-sets`);
          }
          else {
            navigate(`/data/#data-sets`);
          }
        });
      }
    };
    if (data_set_id) {
      fetchDataSet();
    } else {
      setUpdatedDataSet({
        public_id: "",
        name: "",
        description: "",
        type: "",
        title: "",
        event_type: "live",
        price: 0.00,
        moderator_agent_instructions: "",
        auto_moderation: true,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data_set_id]);

  const saveDataSet = async () => {
    if (!canSaveDataSet() || savingDataSet) {
      return;
    }
    setSavingDataSet(true);
    setSaveError(null);

    const saved = await saveUpdatedDataSet();

    if (typeof saved === "string" && saved) {
      // Update url if it's a new event
      window.history.replaceState(null, null, `/data-set/${saved}`);
      swal.fire({
        title: "Success",
        text: "Data set saved successfully.",
        icon: "success",
      });
    } else if (saved && saved.success === false) {
      setSaveError({
        message: saved.message || "Could not save the data set.",
        errors: normalizeMetadataErrors(saved),
      });
    }

    setSavingDataSet(false);
  }

  if (!updatedDataSet && data_set_id) {
    return <Loading>Loading Data Set...</Loading>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "row", backgroundColor: "var(--main-bg)", height: "100%" }}>
      <div style={{ width: "250px" }}>
        <EditDataSetSidebar
          defaultSelectedTab={selectedTab}
          onNavItemClicked={(navItem) => {
            console.log("!!! NAV ITEM CLICKED: ", navItem);
            setSelectedTab(navItem);
          }}
        />
      </div>
      <div
        style={{
          marginLeft: "20px",
          marginRight: "20px",
          width: `calc(100% - 250px)`,
          minHeight: "calc(100vh - 40px)",
          position: "relative",
          backgroundColor: "var(--content-item-bg)",
          padding: "20px",
          borderRadius: "8px",
          color: "var(--main-font-color)",
          zIndex: 1,
          /*height: "2000px",*/
        }}
      >
        <PageHeader style={{ position: "sticky", top: "0px", backgroundColor: "var(--content-item-bg)", zIndex: 2, display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}>
          <div>
            <BackLink onClick={() => {
              if (activeUser?.user_type.match(/client/)) {
                navigate(`/client/data/#data-sets`);
              } else {
                navigate(`/data/#data-sets`);
              }
            }} >
              <i className="fa fa-arrow-left" /> Back to Data Sets</BackLink>
            <PageTitle title={`${updatedDataSet?.id ? "Edit" : "New"} Data Set`} style={{ marginBottom: "0px" }} />
            {updatedDataSet?.name ?
              <PageTitle title={updatedDataSet?.name} style={{ marginBottom: "10px", fontWeight: "500", fontSize: "20px" }} />
              : null}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", flexDirection: "row", gap: "10px" }}>
            <CancelButton onClick={() => {
              setUpdatedDataSet(selectedDataSet);
            }}>Cancel</CancelButton>
            <Button disabled={!canSaveDataSet() || savingDataSet} onClick={saveDataSet} style={{ marginLeft: "auto" }}>{savingDataSet ? "Saving Changes" : "Save Changes"}</Button>
          </div>
        </PageHeader>
        {saveError ? (
          <div
            style={{
              marginTop: "12px",
              padding: "10px 12px",
              fontSize: "13px",
              borderRadius: "6px",
              border: "1px solid #e74c3c",
              backgroundColor: "rgba(231, 76, 60, 0.1)",
              color: "#e74c3c",
            }}
          >
            <div>{saveError.message}</div>
            {saveError.errors.length > 0 ? (
              <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                {saveError.errors.map((error, index) => (
                  <li key={`${error.path}-${index}`}>
                    <code>{error.path}</code>: {error.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div style={{ backgroundColor: "var(--content-item-bg)", marginTop: "20px", marginBottom: "40px", height: "calc(100% - 80px)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", height: "100%" }}>
            <div style={{ width: "100%", height: "100%" }}>
              <CoreDetails
                style={{ display: selectedTab === "details" ? "flex" : "none" }}
                onSelectTab={(tab) => {
                  window.history.replaceState(null, null, `#${tab}`);
                  setSelectedTab(tab);
                }} />
              <DataItems style={{ display: selectedTab === "data-items" ? "flex" : "none" }}
                onSelectTab={(tab) => {
                  window.history.replaceState(null, null, `#${tab}`);
                  setSelectedTab(tab);
                }} />

              <MetadataDetails
                onExportSchema={() => setShowSchemaExport(true)}
                style={{ display: selectedTab === "metadata" ? "flex" : "none" }}
                onSelectTab={(tab) => {
                  window.history.replaceState(null, null, `#${tab}`);
                  setSelectedTab(tab);
                }} />

              {selectedTab === "ingest-workflow" && (
                <IngestWorkflow style={{ display: "flex" }}
                  onSelectTab={(tab) => {
                    window.history.replaceState(null, null, `#${tab}`);
                    setSelectedTab(tab);
                  }} />
              )}
              {selectedTab === "dynamic-workflow" && (
                <DynamicWorkflowGraphTab
                  graph={updatedDataSet?.ingestion_workflow}
                  onGraphChange={(graph) => {
                    setUpdatedDataSet({ ...updatedDataSet, ingestion_workflow: graph });
                  }}
                  generate={{
                    kind: "ingestion",
                    defaultName: updatedDataSet?.name ? `${updatedDataSet.name} ingestion` : "",
                    defaultDescription: updatedDataSet?.description ?? "",
                    clientId: resolveGeneratorClientId(selectedClient, updatedDataSet),
                  }}
                  run={{
                    onRun: () => {
                      if (updatedDataSet?.public_id) runIngestionWorkflow(updatedDataSet.public_id);
                    },
                  }}
                />
              )}

            </div>
          </div>
        </div>
      </div>

      <SchemaExportDialog
        show={showSchemaExport}
        schema={updatedDataSet?.metadata_schema ?? updatedDataSet?.metadataSchema ?? null}
        metadata={updatedDataSet?.metadata ?? null}
        name={updatedDataSet?.name ?? "dataset"}
        onClose={() => setShowSchemaExport(false)}
        onUseSchema={(schema) => setUpdatedDataSet({ ...updatedDataSet, metadata_schema: schema })}
      />
    </div>
  )
}

export default EditDataSet;
